import type { DocPage } from '../types'
import { media } from '../media'


export const configs: DocPage = {
  id: 'configs',
  title: 'Model configurations',
  description:
    'Create, save, reuse, and manage Source and Target model configurations, including defaults, unsaved warnings, and the Create Custom Config entitlement.',
  category: 'Workspace',
  sections: [
    { id: 'overview', title: 'What is a model configuration?' },
    { id: 'library', title: 'Model Configurations library' },
    { id: 'new', title: 'New connection' },
    { id: 'save', title: 'Save and Save as new' },
    { id: 'default', title: 'Default configuration' },
    { id: 'rename-delete', title: 'Rename, edit, and delete' },
    { id: 'unsaved', title: 'Unsaved changes' },
    { id: 'permission', title: 'Create Custom Config permission' },
    { id: 'status', title: 'Status badges' },
    { id: 'workflow', title: 'Recommended workflow' },
  ],
  blocks: [
    {
      type: 'heading',
      level: 2,
      id: 'overview',
      text: 'What is a model configuration?',
    },
    {
      type: 'paragraph',
      text: 'A model configuration (often shortened to config) stores the connection details, filters, and format-specific options used to load a Source or Target model tree. Instead of re-entering host, file path, EPSG, style options, and collection filters every time, you save a named configuration and reopen it from the format dropdown.',
    },
    {
      type: 'paragraph',
      text: 'Configs are separate for Source and Target. Transfer Profiles can reference saved configs so a full transfer setup (source + target + mapping) can be reloaded later. See Transfer Profiles for that packaging layer.',
    },
    {
      type: 'figure',
      src: media.formatDropdown,
      alt: 'Format and model configuration dropdown on Source or Target',
      caption:
        'Figure: Select Format dropdown listing formats (PostGIS, Smallworld, AutoCAD, …) and Create Custom Config (crown when entitlement-gated).',
      size: 'compact',
    },
    {
      type: 'heading',
      level: 2,
      id: 'library',
      text: 'Model Configurations library',
    },
    {
      type: 'paragraph',
      text: 'Open the format control on Source or Target to see the Model Configurations list for that side. The dialog titles use the panel context, for example Source Model Configurations or Target Model Configuration when editing a single item.',
    },
    {
      type: 'table',
      headers: ['UI element', 'Purpose'],
      rows: [
        ['Select Format', 'Opens the format picker and configuration actions for Source or Target.'],
        ['New', 'Starts a fresh connection wizard for the chosen format without loading a saved config.'],
        ['Create Custom Config', 'Opens the save flow to store the current setup as a named configuration (feature-gated).'],
        ['Saved config rows', 'Load a previously saved connection and options for the format.'],
        ['Default badge', 'Marks the configuration used as defaults for new connections of that format.'],
        ['Edit Config', 'Open the kebab/menu action to edit the selected saved configuration.'],
        ['Delete Config', 'Remove a saved configuration after confirmation.'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'new',
      text: 'New connection',
    },
    {
      type: 'steps',
      items: [
        'On Source or Target, open Select Format.',
        'Choose the format (for example PostGIS, AutoCAD, Geopackage). Locked formats require an entitlement — see Account & subscription.',
        'Click New.',
        'Fill the connection form (host/file path, credentials, model mode, style options, and so on).',
        'Optionally open Filters to limit collections and attributes before the model tree loads.',
        'Submit / confirm so X-Translator fetches the model tree.',
      ],
    },
    {
      type: 'callout',
      variant: 'tip',
      title: 'Defaults from a Default config',
      text: 'If a configuration is marked as Default for the format, new connections pre-fill settings from that config. Marking as default uses all settings from that config as the default settings for a new connection.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'save',
      text: 'Save and Save as new',
    },
    {
      type: 'paragraph',
      text: 'After you connect and optionally adjust filters or format options, save the setup so you can reload it later. The Source Model Configuration (or Target Model Configuration) dialog asks you to enter a Configuration Name, then Save Config. You can also Mark as default in the same dialog.',
    },
    {
      type: 'figure',
      src: media.sourceModelConfigurationModal,
      alt: 'Source Model Configuration save dialog',
      caption:
        'Figure: Source Model Configuration — Configuration Name, Save Config, and Mark as default.',
      size: 'compact',
    },
    {
      type: 'table',
      headers: ['Action', 'UI label', 'Behavior'],
      rows: [
        [
          'Overwrite current',
          'Save / Save Config',
          'Writes the current connection and options into the loaded configuration name.',
        ],
        [
          'Create a copy',
          'Save as new / Save as new configuration',
          'Creates another named configuration; use this when you want a variant without replacing the original.',
        ],
        [
          'Mark as default',
          'Mark as default',
          'Uses this configuration’s settings as defaults for new connections of the same format.',
        ],
      ],
    },
    {
      type: 'callout',
      variant: 'tip',
      title: 'Tip from the Save dialog',
      text: 'To save your changes without overwriting, check the “Save as New Configuration” box below.',
    },
    {
      type: 'paragraph',
      text: 'Names must be unique for the format and side. If a name already exists, X-Translator shows an error such as: Error: A configuration named \'…\' already exists.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'default',
      text: 'Default configuration',
    },
    {
      type: 'list',
      items: [
        'Use Mark as default on a saved configuration to promote it.',
        'The list shows a Default badge on the active default.',
        'While editing a default config, the UI notes: This configuration is marked as the default for its format.',
        'Tooltip: Marking as default will use all the settings from this config as the default settings for a new connection.',
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'rename-delete',
      text: 'Rename, edit, and delete',
    },
    {
      type: 'paragraph',
      text: 'Model configurations do not expose a separate Rename Config menu item. To effectively rename, use Save as new configuration with the desired name, then Delete Config on the old entry if you no longer need it. Transfer Profiles use a dedicated Rename Profile action; that is different from model configs.',
    },
    {
      type: 'table',
      headers: ['Action', 'How to do it'],
      rows: [
        ['Edit', 'Choose Edit Config from the configuration menu, adjust fields/filters, then Save.'],
        [
          'Rename (practical)',
          'Save as new configuration with the new name, optionally delete the old configuration.',
        ],
        [
          'Delete',
          'Choose Delete Config. Confirm Are you absolutely sure? before the configuration is removed.',
        ],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'unsaved',
      text: 'Unsaved changes',
    },
    {
      type: 'paragraph',
      text: 'X-Translator tracks whether the live Source/Target setup still matches the last saved configuration. Indicators and warnings help you avoid losing work.',
    },
    {
      type: 'table',
      headers: ['Signal', 'Meaning'],
      rows: [
        ['Unsaved Changes', 'Tooltip/indicator that the current model differs from the saved config.'],
        ['All changes saved', 'Current state matches the saved configuration.'],
        [
          'UNSAVED (Transfer Review)',
          'Review summary tip: Model needs to save as configuration — save before relying on a named config reference.',
        ],
        [
          'Load warning',
          'Title Are you absolutely sure? — You have unsaved changes. Loading a new model will discard your current work… Choose Discard to proceed or cancel to stay.',
        ],
      ],
    },
    {
      type: 'callout',
      variant: 'warning',
      title: 'Switching configs or formats',
      text: 'Loading another configuration, choosing New, or switching formats while Unsaved Changes is active can discard connection edits, filter changes, and (depending on context) related workspace state. Save or Save as new first when you care about the current setup.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'permission',
      text: 'Create Custom Config permission',
    },
    {
      type: 'paragraph',
      text: 'Create Custom Config is gated by the organization feature entitlement create_custom_config. Without it, the action appears locked or unavailable (often with a crown/lock affordance). Contact your administrator to enable the feature on a LIVE X-Translator subscription.',
    },
    {
      type: 'list',
      items: [
        'You can still use New and load existing configurations your organization already shared or that were created when the feature was available.',
        'Saving brand-new named configurations requires Create Custom Config.',
        'After an admin grants the feature, use Refresh Account in the profile menu so entitlements update.',
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'status',
      text: 'Status badges',
    },
    {
      type: 'table',
      headers: ['Badge / status', 'Where you see it', 'Meaning'],
      rows: [
        ['Default', 'Config list', 'This config supplies defaults for new connections of the format.'],
        ['Unsaved Changes', 'Source/Target chrome', 'Live options differ from the last save.'],
        ['All changes saved', 'Source/Target chrome', 'No pending config edits.'],
        ['UNSAVED', 'Transfer Review', 'Model should be saved as a configuration before treating it as persisted.'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'workflow',
      text: 'Recommended workflow',
    },
    {
      type: 'steps',
      items: [
        'Open Select Format → New and connect successfully once.',
        'Tune Filters, Feature Options, style import/export, EPSG, and other format fields until the model tree looks correct.',
        'If entitled, use Create Custom Config / Save Config with a clear Configuration Name (include environment or project in the name).',
        'Optionally Mark as default for the everyday connection of that format.',
        'When you later change filters, Save to overwrite or Save as new configuration for a variant.',
        'Bundle the Source and Target configs into a Transfer Profile when the mapping is also stable.',
      ],
    },
    {
      type: 'callout',
      variant: 'info',
      title: 'Related topics',
      text: 'See Formats for per-format connection fields, Workspace tour for where the dropdown lives, and Profiles & history for associating configs with Transfer Profiles.',
    },
  ],
}
